import { createMutation, createSuspenseQuery } from "@steto-care/steto-query";

import { DeviceRepository } from "@/io/repository/DeviceRepository.ts";
import { DiabetesRepository } from "@/io/repository/DiabetesRepository.ts";
import { RawData } from "@/lib/drivers/DeviceDriver";
import { DeviceDataToUpload } from "@/models/DeviceDataToUpload.ts";
import { MedicalDeviceCreationDTO } from "@/models/DeviceModel.ts";

export class DeviceQueries {
  constructor(
    private readonly deviceRepository: DeviceRepository = new DeviceRepository(),
    private readonly diabetesRepository: DiabetesRepository = new DiabetesRepository(),
  ) {}

  useDevicesByType = createSuspenseQuery(
    "use-device-by-type",
    deviceTypeName => [deviceTypeName],
    async (deviceTypeName: string) => this.deviceRepository.getDevicesByType(deviceTypeName),
    {
      staleTime: 30,
      gcTime: 30,
    },
  );

  useUploadDeviceData = createMutation(
    "use-upload-device-data",
    async (data: DeviceDataToUpload) => {
      const pumpDeviceCreationDTO: MedicalDeviceCreationDTO = {
        serial_number: data.pumpDevice.serial_number,
        device_type_name: data.pumpDevice.device_type_name,
      };
      await this.deviceRepository.createDevice(pumpDeviceCreationDTO);
      if (data.cgmDevice) {
        await this.deviceRepository.createDevice(data.cgmDevice);
      }
      const { last_upload, last_upload_info, ...pumpDeviceWithoutUpdate } = data.pumpDevice;
      await this.deviceRepository.updatePump(pumpDeviceWithoutUpdate);

      const saveGlycemia = async () => {
        if (data.cgmDevice && data.glycemia.length > 0) {
          return await this.diabetesRepository.bulkCreateGlycemia(data.cgmDevice, data.glycemia);
        }
        return undefined;
      };

      await Promise.all([
        this.deviceRepository.bulkCreatePumpEvents(data.pumpDevice, data.pumpEvents),
        this.diabetesRepository.bulkCreateBasalRates(data.pumpDevice, data.basal),
        this.diabetesRepository.bulkCreateInsulin(data.pumpDevice, data.bolus),
        saveGlycemia(),
      ]);

      await this.deviceRepository.updatePump(data.pumpDevice);
      return undefined;
    },
    {
      onSettled: (_, __, variables, { client }) => {
        this.useDevicesByType.invalidate(client, variables.pumpDevice.device_type_name);
      },
    },
  );

  useUploadRawData = createMutation("use-upload-raw-data", async ({ device, raw }: RawData) => {
    await this.deviceRepository.uploadRawData(device, raw);
    return undefined;
  });
}
